import { createApi } from '@reduxjs/toolkit/query/react'
import { GET_APPOINTMENT_BALANCE, MANAGE_APPOINTMENT_CREATE, MANAGE_APPOINTMENT_GUARDIAN_SUBMITTED_FORM_VIEW, MANAGE_APPOINTMENT_LIST, MANAGE_APPOINTMENT_QUESTION_CREATE, MANAGE_APPOINTMENT_QUESTION_DELETE, MANAGE_APPOINTMENT_QUESTION_UPDATE, MANAGE_APPOINTMENT_QUESTIONS_LIST, MANAGE_APPOINTMENT_REMOVE, MANAGE_APPOINTMENT_UPDATE, MANAGE_APPOINTMENT_UPDATE_STATUS, MANAGE_APPOINTMENT_UPDATE_WORKING_HOURS } from 'config/apiPaths';
import { generateCustomPath } from "helpers/common";
import axiosBaseQuery from "utils/axios/axiosBaseQuery"

export const appointmentApi = createApi({
    reducerPath: 'appointmentApi',
    baseQuery: axiosBaseQuery({
        baseUrl: "/appointments",
    }),
    tagTypes: ["AppointmentList", "ClientApptBalance"],
    endpoints: (builder) => ({

        // get appointment list
        getAppointmentList: builder.query({
            query: ({ search = "", appointment_status = "", payment_status = "", appointment_type = "", page = 1, limit = 10 }) => {
                return ({
                    method: "PATCH",
                    url: MANAGE_APPOINTMENT_LIST,
                    data: { search, page, limit, appointment_status, payment_status, appointment_type }
                })
            },
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["AppointmentList"]
        }),

        // update working hours
        updateWorkingHours: builder.mutation({
            query: (data) => {
                return ({
                    method: "PUT",
                    url: MANAGE_APPOINTMENT_UPDATE_WORKING_HOURS,
                    data
                })
            },
        }),

        // update working hours
        getWorkingHours: builder.query({
            query: () => {
                return ({
                    method: "GET",
                    url: MANAGE_APPOINTMENT_UPDATE_WORKING_HOURS,
                })
            },
        }),

        // update status
        updateStatus: builder.mutation({
            query: ({ appointmentId, type, status, data = {} }) => {
                return ({
                    method: "PUT",
                    url: `${MANAGE_APPOINTMENT_UPDATE_STATUS}/${appointmentId}/${type}/${status}`,
                    data
                })
            },
            invalidatesTags: ["AppointmentList", "ClientApptBalance"]
        }),

        // delete appointment
        deleteAppointment: builder.mutation({
            query: (id) => {
                return ({
                    method: "DELETE",
                    url: `${MANAGE_APPOINTMENT_REMOVE}/${id}`
                })
            },
            invalidatesTags: ["AppointmentList", "ClientApptBalance"]
        }),

        // create appointment
        createAppointment: builder.mutation({
            query: (data) => {
                return ({
                    method: "POST",
                    url: MANAGE_APPOINTMENT_CREATE,
                    data
                })
            },
            invalidatesTags: ["AppointmentList", "ClientApptBalance"]
        }),

        // update appointment
        editAppointment: builder.mutation({
            query: ({ id, data }) => {
                return ({
                    method: "PUT",
                    url: `${MANAGE_APPOINTMENT_UPDATE}/${id}`,
                    data
                })
            },
            invalidatesTags: ["AppointmentList", "ClientApptBalance"]
        }),

        getClientApptBalance: builder.query({
            query: (clientId) => ({
                method: "GET",
                url: `${generateCustomPath(GET_APPOINTMENT_BALANCE, { clientId })}`
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["ClientApptBalance"]
        }),

        createAppointmentForm: builder.mutation({
            query: ({ body }) => {
                return ({
                    method: "POST",
                    url: `${MANAGE_APPOINTMENT_QUESTION_CREATE}`,
                    data: body
                })
            },
            invalidatesTags: ["AppointmentQuestionsList"]
        }),

        getAppointmentQuestionsList: builder.query({
            query: ({ id, search, status }) => ({
                method: "PATCH",
                url: `${MANAGE_APPOINTMENT_QUESTIONS_LIST}/${id}`,
                data: {
                    search,
                    status
                }
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["AppointmentQuestionsList"]
        }),

        deleteAppointmentQuestion: builder.mutation({
            query: ({ id }) => {
                return ({
                    method: "DELETE",
                    url: `${MANAGE_APPOINTMENT_QUESTION_DELETE}/${id}`,
                })
            },
            invalidatesTags: ["AppointmentQuestionsList"]
        }),

        updateAppointmentQuestion: builder.mutation({
            query: ({ body, id }) => {
                return ({
                    method: "PATCH",
                    url: `${MANAGE_APPOINTMENT_QUESTION_UPDATE}/${id}`,
                    data: body
                })
            },
            invalidatesTags: ["AppointmentQuestionsList"]
        }),

        getAppointmentGuardianSubmittedForm: builder.query({
            query: ({ id }) => ({
                method: "GET",
                url: `${MANAGE_APPOINTMENT_GUARDIAN_SUBMITTED_FORM_VIEW}/${id}`
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["AppointmentQuestionsList"]
        }),

    }),

})

export const {
    useGetAppointmentListQuery,
    useUpdateWorkingHoursMutation,
    useGetWorkingHoursQuery,
    useUpdateStatusMutation,
    useDeleteAppointmentMutation,
    useCreateAppointmentMutation,
    useEditAppointmentMutation,
    useGetClientApptBalanceQuery,
    useLazyGetClientApptBalanceQuery,
    useCreateAppointmentFormMutation,
    useGetAppointmentQuestionsListQuery,
    useDeleteAppointmentQuestionMutation,
    useUpdateAppointmentQuestionMutation,
    useGetAppointmentGuardianSubmittedFormQuery
} = appointmentApi;